import { useEffect, useState } from "react";
import {
  CheckCircle2,
  ExternalLink,
  Loader2,
  Wallet,
  AlertTriangle,
  Info,
} from "lucide-react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { env } from "@/lib/env";
import { formatMoneyMinor } from "@/lib/money";

type ConnectStatus = "pending" | "restricted" | "enabled" | "disabled" | null;

interface VenueRow {
  id: string;
  venue_name: string;
  country: string;
  stripe_connect_account_id: string | null;
  stripe_connect_status: ConnectStatus;
}

interface PayoutRow {
  id: string;
  amount: number;
  currency: string;
  arrival_date: number; // unix seconds - Stripe's convention
  status: string;
  type: string;
  failure_message: string | null;
}

/**
 * Venue → Payouts. Kicks off Stripe Connect onboarding via the
 * stripe-connect-link Edge Function. Success returns the venue here
 * with ?stripe=done; we then re-fetch the status (which the
 * stripe-webhook function will have flipped to 'enabled' once Stripe
 * marked the account as fully onboarded).
 */
export function VenuePayoutsTab() {
  const { profile } = useAuth();
  const [venue, setVenue] = useState<VenueRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [params, setParams] = useSearchParams();
  const [payouts, setPayouts] = useState<PayoutRow[] | null>(null);
  const [payoutsError, setPayoutsError] = useState<string | null>(null);

  async function load() {
    if (!profile?.venue_application_id) return;
    const { data } = await supabase
      .from("venue_applications")
      .select(
        "id, venue_name, country, stripe_connect_account_id, stripe_connect_status"
      )
      .eq("id", profile.venue_application_id)
      .single();
    setVenue((data ?? null) as VenueRow | null);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.venue_application_id]);

  // Pull recent payouts from Stripe via the venue-scoped Edge Function.
  // We only bother once the venue is connected - pre-onboarding there's
  // nothing to show and the function would short-circuit anyway.
  async function loadPayouts() {
    if (!profile?.venue_application_id) return;
    const { data: sess } = await supabase.auth.getSession();
    const jwt = sess?.session?.access_token;
    if (!jwt) {
      setPayoutsError("Session expired - refresh the page.");
      return;
    }
    try {
      const res = await fetch(
        `${env.supabaseUrl}/functions/v1/stripe-venue-payouts`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ limit: 25 }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPayoutsError(json?.error ?? `Couldn't load payouts (${res.status})`);
        setPayouts([]);
        return;
      }
      setPayouts((json?.payouts ?? []) as PayoutRow[]);
      setPayoutsError(null);
    } catch (e) {
      setPayoutsError(e instanceof Error ? e.message : "Network error");
      setPayouts([]);
    }
  }

  useEffect(() => {
    if (venue?.stripe_connect_status === "enabled") {
      loadPayouts();
    } else {
      setPayouts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue?.stripe_connect_status]);

  // Handle Stripe return callbacks.
  useEffect(() => {
    const s = params.get("stripe");
    if (!s) return;
    if (s === "done") {
      toast.success("Stripe onboarding finished. Finalising your account…");
    } else if (s === "refresh") {
      toast("Onboarding link expired - tap Connect again.", { icon: "↻" });
    }
    params.delete("stripe");
    setParams(params, { replace: true });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function connect() {
    setConnecting(true);
    const { data: sess } = await supabase.auth.getSession();
    const jwt = sess?.session?.access_token;
    if (!jwt) {
      setConnecting(false);
      return toast.error("Session expired - sign in again.");
    }
    try {
      const res = await fetch(
        `${env.supabaseUrl}/functions/v1/stripe-connect-link`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(json?.error || `Stripe setup failed (${res.status})`);
        return;
      }
      if (json?.url) {
        // Stripe takes control; they'll return to /dashboard/payouts?stripe=done
        window.location.href = json.url as string;
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Network error");
    } finally {
      setConnecting(false);
    }
  }

  const status = venue?.stripe_connect_status ?? null;
  const connected = status === "enabled";
  const inProgress = status === "pending" || status === "restricted";

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Venue partner · Payouts
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Payouts</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          We pay you via Stripe Connect the moment each game confirms.
          Your full court fee, no deductions - Whistll's 20% sits on top
          of the player price.
        </p>
      </header>

      {loading ? (
        <PageLoader />
      ) : !venue ? (
        <p className="text-muted-foreground">
          Couldn't load your venue record. Email{" "}
          <a
            href="mailto:hello@whistll.com"
            className="underline underline-offset-2"
          >
            hello@whistll.com
          </a>
          .
        </p>
      ) : (
        <>
          {/* Pre-onboarding disclaimer - surfaces until the account is
              fully enabled. Stripe's hosted onboarding asks for several
              fields across multiple screens (name, phone, address, bank,
              ID verification). Venues who click through quickly without
              filling each field land in a loop where charges_enabled
              stays false and the dashboard keeps showing "Continue
              onboarding". We call that out explicitly so they know to
              scroll every page and finish properly. */}
          {!connected && (
            <div className="mb-6 flex items-start gap-3 rounded-2xl border border-volt/40 bg-volt-subtle p-4">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
              <div className="text-sm">
                <p className="font-semibold">
                  Fill in every field Stripe asks for.
                </p>
                <p className="mt-1 text-muted-foreground">
                  Stripe's onboarding flow has several screens - personal
                  name, phone, address, bank details, and an identity
                  check. Scroll each page, complete every required field,
                  and submit. If you skip any, Stripe can't activate your
                  account and this page will keep showing "Continue
                  onboarding" in a loop.
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Typical flow: tap the button below → fill in all fields
                  on each Stripe screen → submit → you'll be brought back
                  here, and this section will flip to "Connected" within
                  a few seconds (or a few minutes if Stripe needs to
                  review an ID upload).
                </p>
                <p className="mt-3 text-xs text-muted-foreground">
                  Completed everything and still stuck? Email{" "}
                  <a
                    href="mailto:info@whistll.com?subject=Stripe%20onboarding%20stuck"
                    className="font-semibold text-foreground underline underline-offset-2 hover:text-volt"
                  >
                    info@whistll.com
                  </a>{" "}
                  with your venue name and we'll look into it with Stripe
                  directly.
                </p>
              </div>
            </div>
          )}

          <section className="mb-8 rounded-2xl border border-border bg-card p-6">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold uppercase tracking-wider">
                    Stripe connection
                  </p>
                  <StatusPill status={status} />
                </div>
                <p className="mt-1 max-w-lg text-sm text-muted-foreground">
                  {connected
                    ? "You're ready to receive payouts. Confirmed games auto-pay to your bank."
                    : inProgress
                    ? "Stripe is still verifying your details. This usually finishes in minutes; if they need anything else, we'll nudge you."
                    : "Connect your Stripe account to start receiving payouts."}
                </p>
              </div>
              <Button size="lg" onClick={connect} disabled={connecting}>
                {connecting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Opening Stripe…
                  </>
                ) : (
                  <>
                    <Wallet className="h-4 w-4" />
                    {connected
                      ? "Manage on Stripe"
                      : inProgress
                      ? "Continue onboarding"
                      : "Connect Stripe"}
                    <ExternalLink className="h-3 w-3" />
                  </>
                )}
              </Button>
            </div>
            <p className="mt-4 rounded-md border border-border bg-background p-3 text-[11px] text-muted-foreground">
              Stripe's onboarding takes you through identity, business
              verification, and bank-account setup. Whistll never sees your
              banking details.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Payout history
            </h2>
            <PayoutHistory
              connected={connected}
              payouts={payouts}
              error={payoutsError}
            />
          </section>
        </>
      )}
    </div>
  );
}

function PayoutHistory({
  connected,
  payouts,
  error,
}: {
  connected: boolean;
  payouts: PayoutRow[] | null;
  error: string | null;
}) {
  if (!connected) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card/40 p-8 text-center">
        <p className="text-sm font-semibold">Not connected yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Finish Stripe onboarding above and your payouts will appear here.
        </p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-[#f87171]/30 bg-[rgba(248,113,113,0.05)] p-6 text-sm">
        <p className="font-semibold">Couldn't load payouts</p>
        <p className="mt-1 text-muted-foreground">{error}</p>
      </div>
    );
  }
  if (!payouts) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-border bg-card/40 py-10">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (payouts.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card/40 p-8 text-center">
        <p className="text-sm font-semibold">No payouts yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Your first payout lands here the moment your first confirmed
          game pays out. Stripe runs payouts on a 2-day rolling schedule
          by default.
        </p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full min-w-[560px] text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/40 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5">Arrival</th>
            <th className="px-4 py-2.5">Amount</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5">Type</th>
          </tr>
        </thead>
        <tbody>
          {payouts.map((p) => (
            <tr
              key={p.id}
              className="border-b border-border/40 last:border-b-0"
            >
              <td className="px-4 py-2.5 text-muted-foreground">
                {new Date(p.arrival_date * 1000).toLocaleDateString(
                  undefined,
                  { day: "numeric", month: "short", year: "numeric" },
                )}
              </td>
              <td className="px-4 py-2.5 font-semibold">
                {formatMoneyMinor(p.amount, p.currency)}
              </td>
              <td className="px-4 py-2.5">
                <PayoutStatusPill status={p.status} />
                {p.failure_message && (
                  <p className="mt-0.5 text-[10px] text-[#f87171]">
                    {p.failure_message}
                  </p>
                )}
              </td>
              <td className="px-4 py-2.5 text-xs text-muted-foreground">
                {p.type}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayoutStatusPill({ status }: { status: string }) {
  // Stripe payout statuses: pending, in_transit, paid, failed, canceled.
  const cls =
    status === "paid"
      ? "bg-volt/15 text-volt"
      : status === "in_transit" || status === "pending"
        ? "bg-[rgba(251,191,36,0.15)] text-[#fbbf24]"
        : status === "failed" || status === "canceled"
          ? "bg-[rgba(248,113,113,0.15)] text-[#f87171]"
          : "bg-secondary text-muted-foreground";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function StatusPill({ status }: { status: ConnectStatus }) {
  if (status === "enabled") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
        <CheckCircle2 className="h-2.5 w-2.5" /> Connected
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(251,191,36,0.15)] px-2 py-0.5 text-[10px] font-bold text-[#fbbf24]">
        <Loader2 className="h-2.5 w-2.5" /> Verifying
      </span>
    );
  }
  if (status === "restricted") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(248,113,113,0.15)] px-2 py-0.5 text-[10px] font-bold text-[#f87171]">
        <AlertTriangle className="h-2.5 w-2.5" /> Needs attention
      </span>
    );
  }
  return (
    <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
      Not connected
    </span>
  );
}
